import React, { useEffect, useState } from 'react';
import { TrendingUp, Loader2, ChevronRight, ArrowUp, ArrowDown, Pause, Shuffle, Sparkles } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

// Map per-exercise actions to icons + colours
const ACTION_META = {
  bump:     { icon: ArrowUp,   colour: 'text-[#BEFF38]', label: 'BUMP' },
  hold:     { icon: Pause,     colour: 'text-zinc-300',  label: 'HOLD' },
  deload:   { icon: ArrowDown, colour: 'text-amber-400', label: 'DELOAD' },
  swap:     { icon: Shuffle,   colour: 'text-rose-400',  label: 'SWAP' },
  baseline: { icon: Sparkles,  colour: 'text-[#ff7a1a]', label: 'BASELINE' },
};

const ProgressionStrip = ({ refreshKey = 0 }) => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.progressionStatus()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [refreshKey]);

  const advance = async () => {
    if (!data?.advance_ready) return;
    setAdvancing(true);
    try {
      const r = await api.progressionAdvance();
      toast({ title: 'Stage advanced', description: r.stage_label });
      refresh();
    } catch (e) {
      toast({ title: 'Could not advance', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setAdvancing(false); }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 flex items-center gap-2 text-zinc-400 text-sm" data-testid="progression-strip-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Reading your week…
      </div>
    );
  }
  if (!data) return null;

  const topActions = (data.per_exercise || []).filter((a) => a.action !== 'hold').slice(0, 3);

  return (
    <div
      data-testid="progression-strip"
      className="rounded-xl border border-zinc-800 bg-gradient-to-br from-zinc-900/80 to-zinc-900/30 p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <TrendingUp className="w-4 h-4 text-[#BEFF38]" />
          <span className="text-[10px] uppercase tracking-wider font-black text-[#BEFF38]">
            Week {data.week_number} · {data.stage_label}
          </span>
          {data.today_slot && data.today_slot !== 'MIXED' && (
            <span
              data-testid="today-slot-pill"
              className="px-2 py-0.5 rounded-full bg-[#ff7a1a] text-[#0b0b0c] text-[10px] font-black uppercase tracking-wider"
              title={data.training_split_label}
            >
              Today: {data.today_slot.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">
          {data.compliance_pct}% compliance
        </span>
      </div>

      {/* Phase-advance prompt */}
      {data.plan_type === 'transform' && (data.advance_ready || data.advance_reason) && (
        <div className={`rounded-lg border p-3 ${
          data.advance_ready ? 'border-[#BEFF38]/40 bg-[#BEFF38]/5' : 'border-zinc-800 bg-zinc-900/40'
        }`} data-testid="advance-prompt">
          <p className="text-xs text-white font-bold">{data.advance_reason}</p>
          {data.advance_ready && (
            <button
              onClick={advance}
              disabled={advancing}
              data-testid="advance-confirm-btn"
              className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#BEFF38] text-black text-[11px] font-black uppercase tracking-wider hover:bg-[#a6e62e] disabled:opacity-50"
            >
              {advancing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Advance to next stage
            </button>
          )}
        </div>
      )}

      {/* Top actionable items */}
      {topActions.length > 0 ? (
        <ul className="space-y-1.5">
          {topActions.map((a) => {
            const meta = ACTION_META[a.action] || ACTION_META.hold;
            const Icon = meta.icon;
            return (
              <li
                key={a.exercise}
                data-testid={`progression-action-${a.action}`}
                className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-[#0b0c10] px-3 py-2"
              >
                <Icon className={`w-4 h-4 ${meta.colour} shrink-0`} />
                <span className={`text-[10px] font-black uppercase tracking-wider ${meta.colour} w-16 shrink-0`}>{meta.label}</span>
                <span className="text-xs text-white font-semibold truncate flex-1">{a.exercise}</span>
                <span className="text-[11px] text-zinc-400 hidden sm:inline truncate max-w-[180px]">{a.headline}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-xs text-zinc-500 italic">Log your first set — the engine starts adapting from set one.</p>
      )}
    </div>
  );
};

export default ProgressionStrip;
